import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import heroLifestyle from "@/assets/hero-lifestyle.jpg";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const { isRTL } = useLanguage();

  const scrollToHowItWorks = () => {
    const section = document.getElementById("how-it-works");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative overflow-hidden min-h-[90vh] flex items-center">
      {/* Background image */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover"
        style={{
          backgroundImage: `url(${heroLifestyle})`,
          backgroundPosition: isRTL ? "left center" : "right center",
          filter: "brightness(1.1) saturate(1.05)",
        }}
      />

      {/* Gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isRTL
            ? "linear-gradient(to left, rgba(13,31,53,0.92) 0%, rgba(13,31,53,0.6) 45%, rgba(13,31,53,0.0) 100%)"
            : "linear-gradient(to right, rgba(13,31,53,0.92) 0%, rgba(13,31,53,0.6) 45%, rgba(13,31,53,0.0) 100%)",
        }}
        aria-hidden="true"
      />

      {/* Foreground content */}
      <div className="relative z-10 w-full pt-24 md:pt-32 pb-16 md:pb-24">
        <div className={`max-w-4xl mx-auto px-4 md:px-6 md:max-w-6xl ${isRTL ? 'text-right md:pr-12' : 'text-left md:pl-12'}`}>
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-heading font-semibold leading-[1.1] tracking-tight text-white fade-in-slow md:max-w-[60%]"
            style={isRTL ? { fontFamily: "'Segoe UI', Tahoma, Arial, sans-serif" } : undefined}
          >
            The Smarter Way to Pay for Life's Big Moments
          </h1>

          <p
            className="mt-6 max-w-xl text-lg md:text-xl text-white/80 leading-relaxed font-body fade-in-slow"
            style={{
              animationDelay: "0.1s",
              ...(isRTL ? { fontFamily: "'Segoe UI', Tahoma, Arial, sans-serif" } : {}),
            }}
          >
            Compare flexible payment plans for travel, education and healthcare — from UAE's leading BNPL and bank instalment providers, all in one place.
          </p>

          {/* CTA buttons */}
          <div
            className={`mt-10 md:mt-12 flex flex-wrap items-center gap-4 fade-in-slow ${isRTL ? 'justify-end' : 'justify-start'}`}
            style={{ animationDelay: "0.2s" }}
          >
            <Button
              size="lg"
              className="font-medium"
              onClick={() => navigate("/travel")}
            >
              Search Travel Now
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-white/20 text-white hover:bg-white/10 hover:text-white font-medium"
              onClick={scrollToHowItWorks}
            >
              How It Works
            </Button>
          </div>

          {/* Trust indicators */}
          <div
            className={`mt-8 flex items-center gap-4 text-sm text-white/50 fade-in-slow ${isRTL ? 'justify-end' : 'justify-start'}`}
            style={{ animationDelay: "0.3s" }}
          >
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/80" />
              BNPL up to AED 20K
            </span>
            <span className="w-px h-4 bg-white/20" />
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/80" />
              Bank Instalments Available
            </span>
            <span className="w-px h-4 bg-white/20" />
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/80" />
              256-bit Encrypted & Secure
            </span>
          </div>

          {/* Regulatory trust line */}
          <p
            className="mt-4 text-[12px] text-white/40 font-body text-center md:text-left max-w-xl fade-in-slow"
            style={{ animationDelay: "0.4s" }}
          >
            Financing options subject to UAE Central Bank regulations and individual provider approval. Flexaura is a comparison and referral marketplace — not a lender.
          </p>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
